import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Navbar, MobileNav } from "@/components/Navbar";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { BarChart3, Link2, Loader2, Activity, Download } from "lucide-react";
import { CreatorAnalytics } from "@/components/analytics/CreatorAnalytics";
import { BusinessAnalytics } from "@/components/analytics/BusinessAnalytics";
import { BusinessReport } from "@/components/analytics/BusinessReport";

export default function Analytics() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isBusiness = user?.role === "business";

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const url = isBusiness ? "/analytics/business" : "/analytics/creator";
    api.get(url).then((r) => setData(r.data)).catch(() => setData(null)).finally(() => setLoading(false));
  }, [isBusiness]);

  const socialConnected = data?.social?.connected;
  const connectPath = isBusiness ? "/business/profile" : "/creator/profile";

  return (
    <div className="min-h-screen overflow-x-hidden bg-[#FAF9F6] nf-pb-nav">
      <Navbar />
      <div className="mx-auto max-w-6xl px-4 py-8 sm:px-6 lg:px-8">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <div className="flex items-center gap-2">
              <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-zinc-900 text-white"><BarChart3 className="h-5 w-5" /></div>
              <h1 className="font-display text-3xl font-bold tracking-tight text-zinc-900">Analytics</h1>
            </div>
            <p className="mt-2 text-zinc-500">
              {isBusiness
                ? "Your real Nearfluence activity — campaigns, applications, creators and event reach."
                : "Your real Nearfluence activity — applications, collaborations and events."}
            </p>
          </div>
          {data && (
            <div className="flex items-center gap-2">
              <span data-testid="analytics-name" className="hidden items-center gap-1.5 rounded-full border border-zinc-200 bg-white px-3 py-1.5 text-sm font-medium text-zinc-600 sm:inline-flex">
                <Activity className="h-4 w-4 text-emerald-500" /> {isBusiness ? data.business_name : data.creator_name}
              </span>
              {isBusiness && (
                <Button data-testid="analytics-download-report" variant="outline" onClick={() => window.print()} className="rounded-full">
                  <Download className="mr-2 h-4 w-4" /> Download report
                </Button>
              )}
            </div>
          )}
        </div>

        {loading ? (
          <div className="flex h-64 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-emerald-600" /></div>
        ) : !data ? (
          <div className="mt-8 rounded-2xl border border-zinc-200 bg-white p-10 text-center text-zinc-500">Couldn't load your analytics. Please try again.</div>
        ) : (
          <>
            {!socialConnected && (
              <div data-testid="analytics-connect-banner" className="mt-5 flex flex-col gap-3 rounded-2xl border border-emerald-200 bg-emerald-50/60 p-4 sm:flex-row sm:items-center">
                <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-200 bg-white px-2.5 py-1 text-xs font-semibold text-emerald-700"><Activity className="h-3.5 w-3.5" /> Live data</span>
                <p className="flex-1 text-sm text-emerald-900">
                  The figures below are real, drawn from your Nearfluence activity. Connect Instagram{isBusiness ? " or TikTok" : ""} to also unlock {isBusiness ? "follower, reach & audience insights" : "verified follower & reach insights"}.
                </p>
                <Button data-testid="analytics-connect-btn" onClick={() => navigate(connectPath)} className="rounded-full bg-emerald-600 hover:bg-emerald-700">
                  <Link2 className="mr-2 h-4 w-4" /> Connect {isBusiness ? "a platform" : "Instagram"}
                </Button>
              </div>
            )}
            <div className="mt-6">
              {isBusiness
                ? <BusinessAnalytics data={data} onConnect={() => navigate(connectPath)} />
                : <CreatorAnalytics data={data} onConnect={() => navigate(connectPath)} />}
            </div>
            {isBusiness && <BusinessReport data={data} />}
          </>
        )}
      </div>
      <MobileNav />
    </div>
  );
}
